// onMarsGuidedTutorial.ts
// Moduł prowadzący gracza przez przygotowanie i pierwszą partię On Mars.
// Treść jest autorskim opracowaniem zasad, przeznaczonym do użycia w aplikacji.

export type GuidedChoice = {
  id: string;
  label: string;
  next: string;
  hint?: string;
};

export type GuidedStep = {
  id: string;
  section: string;
  title: string;
  instruction: string;
  explanation?: string;
  checklist?: string[];
  warnings?: string[];
  choices?: GuidedChoice[];
  next?: string;
  tags: string[];
};

export const onMarsGuidedTutorial: GuidedStep[] = [
  // ──────────────────────────────────────────────────────────────────────────
  // START
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "intro-001",
    section: "start",
    title: "Wybierz tryb",
    instruction:
      "Ten przewodnik może przeprowadzić was przez przygotowanie gry albo towarzyszyć wam podczas pierwszej partii.",
    choices: [
      { id: "setup", label: "Przygotowanie gry", next: "setup-001" },
      { id: "play", label: "Rozpocznij pierwszą partię", next: "round-001" },
      { id: "turn", label: "Pomoc w mojej turze", next: "turn-001" },
      { id: "travel", label: "Faza wahadłowca", next: "shuttle-001" },
      { id: "end", label: "Koniec gry i punktacja", next: "end-001" },
    ],
    tags: ["start", "menu"],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // SETUP – WSPÓLNY
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "setup-001",
    section: "setup",
    title: "Połóż planszę główną",
    instruction:
      "Połóż planszę główną na środku stołu. Lewa część przedstawia stację orbitalną, a pozostała część kolonię na Marsie.",
    checklist: [
      "Każdy gracz dosięga planszy.",
      "Po obu stronach planszy jest miejsce na plansze graczy.",
    ],
    next: "setup-002",
    tags: ["setup", "plansza"],
  },
  {
    id: "setup-002",
    section: "setup",
    title: "Przygotuj planszę budynków",
    instruction:
      "Połóż ekspozytor płytek budynków obok planszy i umieść na nim płytki podstawowych budynków według typów.",
    explanation:
      "Płytki pozostają we wspólnej puli. Gdy zabraknie standardowych znaczników zasobów lub kryształów, można użyć zamienników.",
    next: "setup-003",
    tags: ["setup", "budynki"],
  },
  {
    id: "setup-003",
    section: "setup",
    title: "Przygotuj początkowe budynki",
    instruction:
      "Umieść pięć początkowych płytek budynków na wskazanych polach mapy Marsa. Każdy typ LSS zaczyna od jednego budynku.",
    warnings: [
      "Użyj diagramu przygotowania dla właściwej liczby graczy.",
      "Nie zastępuj początkowych płytek zwykłymi płytkami budynków.",
    ],
    next: "setup-004",
    tags: ["setup", "LSS", "mapa"],
  },
  {
    id: "setup-004",
    section: "setup",
    title: "Ustaw tory LSS",
    instruction:
      "Umieść cztery znaczniki LSS na początkowych polach odpowiadających tlenowi, wodzie, roślinom i energii.",
    explanation:
      "Kopalnie nie mają osobnego toru LSS. Dostarczają minerałów potrzebnych przede wszystkim do budowy generatorów.",
    next: "setup-005",
    tags: ["setup", "LSS"],
  },
  {
    id: "setup-005",
    section: "setup",
    title: "Ustaw poziom kolonii",
    instruction:
      "Umieść znacznik poziomu kolonii na poziomie początkowym.",
    explanation:
      "Poziom kolonii wzrasta, gdy wszystkie wymagane znaczniki LSS osiągną wskazane wartości.",
    next: "setup-006",
    tags: ["setup", "poziom kolonii"],
  },
  {
    id: "setup-006",
    section: "setup",
    title: "Przygotuj nagrody LSS",
    instruction:
      "Potasuj płytki nagród LSS i umieść odkryte płytki w odpowiednich miejscach toru.",
    explanation:
      "Gracz rozwijający słabiej rozwinięty system LSS otrzyma jedną z dostępnych nagród.",
    next: "setup-007",
    tags: ["setup", "LSS", "nagrody"],
  },
  {
    id: "setup-007",
    section: "setup",
    title: "Przygotuj technologie",
    instruction:
      "Rozłóż technologie w siatce technologii zgodnie z diagramem przygotowania. Technologie schronień umieść w przeznaczonym dla nich miejscu.",
    warnings: [
      "Układ i liczba dostępnych płytek zależą od liczby graczy.",
    ],
    next: "setup-008",
    tags: ["setup", "technologie"],
  },
  {
    id: "setup-008",
    section: "setup",
    title: "Przygotuj magazyn orbitalny",
    instruction:
      "Uzupełnij rzędy magazynu orbitalnego zasobami oraz kryształami zgodnie z diagramem dla liczby graczy.",
    explanation:
      "W akcji Zaopatrzenia wybiera się jeden rząd magazynu i zabiera dostępne z niego elementy zgodnie z siłą akcji.",
    warnings: [
      "W grze dwuosobowej część rzędów nie jest używana.",
    ],
    next: "setup-009",
    tags: ["setup", "magazyn", "zasoby"],
  },
  {
    id: "setup-009",
    section: "setup",
    title: "Przygotuj projekty",
    instruction:
      "Potasuj karty projektów i wyłóż początkową ofertę projektów.",
    explanation:
      "Projekt zapewnia natychmiastową premię i pozwala później ulepszyć zgodny budynek podstawowy.",
    next: "setup-010",
    tags: ["setup", "projekty"],
  },
  {
    id: "setup-010",
    section: "setup",
    title: "Przygotuj naukowców",
    instruction:
      "Potasuj karty naukowców, wyłóż odpowiednią liczbę kart i połóż na nich pasujące znaczniki naukowców.",
    explanation:
      "Kontrakty ziemskie nie muszą być dostępne od początku. Pojawią się po zatrudnieniu pierwszego naukowca.",
    next: "setup-011",
    tags: ["setup", "naukowcy"],
  },
  {
    id: "setup-011",
    section: "setup",
    title: "Przygotuj kontrakty",
    instruction:
      "Potasuj kontrakty ziemskie i połóż talię zakrytą przy planszy naukowców i kontraktów.",
    warnings: [
      "Nie wykładaj kontraktów na ofertę, dopóki nie uruchomi tego zatrudnienie pierwszego naukowca.",
    ],
    next: "setup-012",
    tags: ["setup", "kontrakty"],
  },
  {
    id: "setup-012",
    section: "setup",
    title: "Przygotuj badania i odkrycia",
    instruction:
      "Potasuj płytki badań oraz płytki odkryć osobno. Rozmieść je zakryte na wskazanych polach mapy.",
    explanation:
      "Łaziki będą je odkrywać i zbierać podczas eksploracji.",
    warnings: [
      "Nie podglądaj zakrytych płytek.",
    ],
    next: "setup-013",
    tags: ["setup", "łazik", "badania", "odkrycia"],
  },
  {
    id: "setup-013",
    section: "setup",
    title: "Wybierz misje",
    instruction:
      "Wybierz wymagane trzy misje DOME, umieść ich karty przy planszy i ustaw na nich znaczniki postępu.",
    explanation:
      "Gra jest bliska końca, gdy wymagane misje zostaną ukończone. Wkład w misję daje kryształy.",
    next: "setup-014",
    tags: ["setup", "misje"],
  },
  {
    id: "setup-014",
    section: "setup",
    title: "Ustaw licznik pozostałych misji",
    instruction:
      "Umieść znacznik pozostałych misji na początkowej wartości.",
    explanation:
      "Wymagana liczba ukończonych misji może zmniejszać się wraz ze wzrostem poziomu kolonii.",
    next: "setup-015",
    tags: ["setup", "misje", "koniec gry"],
  },
  {
    id: "setup-015",
    section: "setup",
    title: "Ustaw wahadłowiec",
    instruction:
      "Umieść wahadłowiec na początkowym polu toru podróży i ustaw znacznik zgodnie z początkowym poziomem kolonii.",
    explanation:
      "Po każdej fazie kolonizacji wahadłowiec porusza się i może zabierać graczy między Orbitą a Marsem.",
    next: "setup-016",
    tags: ["setup", "wahadłowiec"],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // SETUP – GRACZ
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "setup-016",
    section: "setup-player",
    title: "Wybierz kolor",
    instruction:
      "Każdy gracz wybiera kolor i bierze planszę gracza oraz wszystkie elementy tego koloru.",
    checklist: [
      "Plansza gracza.",
      "Znacznik gracza i znacznik punktacji.",
      "Koloniści.",
      "Boty.",
      "Łazik.",
      "Statki.",
      "Znaczniki postępu.",
      "Znaczniki budynków zaawansowanych.",
      "Schronienia.",
    ],
    next: "setup-017",
    tags: ["setup", "gracz"],
  },
  {
    id: "setup-017",
    section: "setup-player",
    title: "Przygotuj kolonistów",
    instruction:
      "Umieść początkowych kolonistów w odpowiednich obszarach planszy gracza, a pozostałych na torze kwater mieszkalnych.",
    explanation:
      "Koloniści w kwaterach są dostępni. Koloniści w obszarze pracy nie są dostępni, dopóki ich nie odzyskasz.",
    warnings: [
      "Nie wszyscy koloniści są dostępni od początku gry.",
    ],
    next: "setup-018",
    tags: ["setup", "koloniści", "kwatery"],
  },
  {
    id: "setup-018",
    section: "setup-player",
    title: "Przygotuj boty",
    instruction:
      "Umieść początkowego bota na mapie, a pozostałe boty na wyznaczonych polach planszy gracza.",
    explanation:
      "Boty wyznaczają strefę, w której możesz budować i ulepszać.",
    warnings: [
      "Użyj pozycji początkowej wskazanej dla liczby graczy.",
    ],
    next: "setup-019",
    tags: ["setup", "bot"],
  },
  {
    id: "setup-019",
    section: "setup-player",
    title: "Przygotuj łazik",
    instruction:
      "Umieść swój łazik na jego polu początkowym na mapie Marsa.",
    explanation:
      "Łazik służy do zbierania kryształów, badań i odkryć. Nie wyznacza zasięgu budowy.",
    next: "setup-020",
    tags: ["setup", "łazik"],
  },
  {
    id: "setup-020",
    section: "setup-player",
    title: "Przygotuj statki",
    instruction:
      "Umieść statki na wyznaczonych polach planszy gracza. Dostęp do kolejnych statków uzyskasz podczas rozwoju kolonii.",
    explanation:
      "Statki mogą służyć do prywatnej podróży i punktują na końcu gry, jeżeli znajdują się w hangarze.",
    next: "setup-021",
    tags: ["setup", "statki"],
  },
  {
    id: "setup-021",
    section: "setup-player",
    title: "Przygotuj znaczniki postępu",
    instruction:
      "Umieść swoje znaczniki postępu na początkowych miejscach wskazanych przez planszę i misje.",
    explanation:
      "Znaczniki postępu służą m.in. do zaznaczania udziału w misjach i pozycji w obszarze postępu.",
    next: "setup-022",
    tags: ["setup", "postęp", "misje"],
  },
  {
    id: "setup-022",
    section: "setup-player",
    title: "Przygotuj schronienia i budynki zaawansowane",
    instruction:
      "Umieść schronienia i znaczniki budynków zaawansowanych na odpowiednich polach planszy gracza.",
    explanation:
      "Odsłanianie pól może zapewniać premie i wpływać na punktację końcową.",
    next: "setup-023",
    tags: ["setup", "schronienia", "budynki zaawansowane"],
  },
  {
    id: "setup-023",
    section: "setup-player",
    title: "Rozdaj cele prywatne",
    instruction:
      "Każdy otrzymuje cele prywatne zgodnie z przygotowaniem i zachowuje je w tajemnicy.",
    explanation:
      "W całej grze możesz zrealizować tylko jeden cel prywatny. Inną kartę można odrzucić jako kryształ.",
    next: "setup-024",
    tags: ["setup", "cel prywatny"],
  },
  {
    id: "setup-024",
    section: "setup-player",
    title: "Ustal początkową kolejność",
    instruction:
      "Ustal kolejność graczy i umieść znaczniki na odpowiednich polach kolejności po stronie Orbity.",
    explanation:
      "Pola kolejności dają różne początkowe korzyści. Rozpatrz je w prawidłowej kolejności.",
    next: "setup-025",
    tags: ["setup", "kolejność"],
  },
  {
    id: "setup-025",
    section: "setup-player",
    title: "Pobierz początkowe korzyści",
    instruction:
      "Każdy gracz pobiera zasoby lub inne korzyści wynikające z jego początkowego pola kolejności.",
    warnings: [
      "Nie wszyscy gracze zaczynają z identycznym zestawem zasobów.",
    ],
    next: "setup-026",
    tags: ["setup", "zasoby", "kolejność"],
  },
  {
    id: "setup-026",
    section: "setup",
    title: "Sprawdzenie końcowe",
    instruction:
      "Przed pierwszą turą sprawdźcie wspólnie wszystkie najważniejsze obszary.",
    checklist: [
      "Trzy misje są widoczne.",
      "Wahadłowiec i poziom kolonii są ustawione.",
      "Magazyn orbitalny jest uzupełniony.",
      "Technologie i projekty są dostępne.",
      "Naukowcy są wyłożeni, a kontrakty czekają zakryte.",
      "Każdy ma kolonistów, bota, łazik i zasoby początkowe.",
      "Znaczniki graczy stoją na polach kolejności Orbity.",
    ],
    next: "round-001",
    tags: ["setup", "kontrola"],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // PIERWSZA RUNDA
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "round-001",
    section: "first-round",
    title: "Rozpoczyna się faza kolonizacji",
    instruction:
      "Gracze wykonują po jednej turze zgodnie z kolejnością na aktualnej stronie planszy.",
    explanation:
      "Na początku gry wszyscy zwykle znajdują się na Orbicie, więc korzystają z akcji orbitalnych.",
    next: "round-002",
    tags: ["pierwsza runda", "kolonizacja"],
  },
  {
    id: "round-002",
    section: "first-round",
    title: "Wybierz pierwszą akcję",
    instruction:
      "W pierwszej partii wybierz akcję, która przygotuje konkretny plan na Marsie.",
    choices: [
      {
        id: "blueprint",
        label: "Weź projekt",
        next: "orbit-blueprint-001",
        hint: "Dobre, jeśli widzisz projekt zgodny z budynkiem, który szybko ulepszysz.",
      },
      {
        id: "tech",
        label: "Pozyskaj lub rozwiń technologię",
        next: "orbit-tech-001",
        hint: "Dobre, jeśli planujesz tworzyć kompleks.",
      },
      {
        id: "supply",
        label: "Pobierz zaopatrzenie",
        next: "orbit-supply-001",
        hint: "Dobre, jeśli brakuje ci zasobów do pierwszej budowy.",
      },
    ],
    tags: ["pierwsza runda", "wybór akcji"],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // POMOC W TURZE
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "turn-001",
    section: "turn-helper",
    title: "Gdzie znajduje się twój znacznik?",
    instruction:
      "Wybierz aktualną stronę planszy.",
    choices: [
      { id: "orbit", label: "Orbita", next: "turn-orbit-001" },
      { id: "mars", label: "Mars", next: "turn-mars-001" },
    ],
    tags: ["tura", "pomoc"],
  },
  {
    id: "turn-orbit-001",
    section: "turn-helper",
    title: "Akcje dostępne na Orbicie",
    instruction:
      "Wybierz akcję główną, którą chcesz wykonać.",
    choices: [
      { id: "pod", label: "Lądownik", next: "orbit-pod-001" },
      { id: "blueprint", label: "Pozyskanie projektu", next: "orbit-blueprint-001" },
      { id: "tech", label: "Badania i rozwój", next: "orbit-tech-001" },
      { id: "supply", label: "Zaopatrzenie", next: "orbit-supply-001" },
    ],
    tags: ["orbita", "akcje"],
  },
  {
    id: "turn-mars-001",
    section: "turn-helper",
    title: "Akcje dostępne na Marsie",
    instruction:
      "Wybierz akcję główną, którą chcesz wykonać.",
    choices: [
      { id: "control", label: "Centrum kontroli", next: "mars-control-001" },
      { id: "build", label: "Budowa", next: "mars-build-001" },
      { id: "upgrade", label: "Ulepszenie budynku", next: "mars-upgrade-001" },
      { id: "ship", label: "Przyjęcie statku", next: "mars-ship-001" },
      { id: "scientist", label: "Naukowiec lub kontrakt", next: "mars-scientist-001" },
    ],
    tags: ["Mars", "akcje"],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // ORBITA – LĄDOWNIK
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "orbit-pod-001",
    section: "orbit-action",
    title: "Lądownik: sprawdź cel",
    instruction:
      "Użyj lądownika, gdy chcesz natychmiast przenieść się z Orbity na Marsa zamiast czekać na wahadłowiec.",
    warnings: [
      "To rozwiązanie awaryjne i zwykle mniej wydajne niż dobrze zaplanowana podróż.",
    ],
    next: "orbit-pod-002",
    tags: ["orbita", "lądownik"],
  },
  {
    id: "orbit-pod-002",
    section: "orbit-action",
    title: "Lądownik: opłać koszt",
    instruction:
      "Opłać koszt lądowania wskazany przy akcji i przenieś znacznik gracza na właściwe pole po stronie Marsa.",
    next: "orbit-pod-003",
    tags: ["orbita", "lądownik", "koszt"],
  },
  {
    id: "orbit-pod-003",
    section: "orbit-action",
    title: "Lądownik: rozpatrz przybycie",
    instruction:
      "Rozpatrz wszystkie efekty przybycia na Marsa wymagane przez aktualny stan kolonii i wybrane pole kolejności.",
    next: "turn-end-001",
    tags: ["orbita", "lądownik", "Mars"],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // ORBITA – PROJEKT
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "orbit-blueprint-001",
    section: "orbit-action",
    title: "Projekt: wybierz kartę",
    instruction:
      "Wybierz jeden dostępny projekt z wystawy.",
    checklist: [
      "Wiesz, jaki typ budynku podstawowego będzie ulepszany.",
      "Masz lub planujesz mieć dostęp bota do właściwego budynku.",
      "Potrafisz spełnić wymóg wielkości kompleksu projektu.",
    ],
    warnings: [
      "Niezbudowany projekt może przynieść punkty ujemne.",
    ],
    next: "orbit-blueprint-002",
    tags: ["orbita", "projekt"],
  },
  {
    id: "orbit-blueprint-002",
    section: "orbit-action",
    title: "Projekt: opłać akcję",
    instruction:
      "Umieść wymaganego kolonistę na polu akcji albo opłać zwiększenie siły akcji zgodnie z zajęciem pola.",
    explanation:
      "Obecność cudzych kolonistów może zwiększać koszt wykonania akcji.",
    next: "orbit-blueprint-003",
    tags: ["orbita", "projekt", "kolonista"],
  },
  {
    id: "orbit-blueprint-003",
    section: "orbit-action",
    title: "Projekt: weź kartę",
    instruction:
      "Weź wybraną kartę projektu i połóż ją odkrytą przy swojej planszy.",
    next: "orbit-blueprint-004",
    tags: ["orbita", "projekt"],
  },
  {
    id: "orbit-blueprint-004",
    section: "orbit-action",
    title: "Projekt: odbierz premię",
    instruction:
      "Natychmiast otrzymaj premię przedstawioną na projekcie.",
    warnings: [
      "Premia nie oznacza, że budynek został już zbudowany.",
    ],
    next: "orbit-blueprint-005",
    tags: ["orbita", "projekt", "premia"],
  },
  {
    id: "orbit-blueprint-005",
    section: "orbit-action",
    title: "Projekt: uzupełnij ofertę",
    instruction:
      "Uzupełnij puste miejsce wystawy zgodnie z bieżącym poziomem kolonii i zasadami dostępności projektów.",
    next: "turn-end-001",
    tags: ["orbita", "projekt", "wystawa"],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // ORBITA – TECHNOLOGIE
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "orbit-tech-001",
    section: "orbit-action",
    title: "Technologia: wybierz wariant",
    instruction:
      "Zdecyduj, czy pozyskujesz nową technologię, czy rozwijasz już posiadaną.",
    choices: [
      { id: "learn", label: "Pozyskaj nową technologię", next: "orbit-tech-learn-001" },
      { id: "upgrade", label: "Rozwiń technologię", next: "orbit-tech-upgrade-001" },
    ],
    tags: ["orbita", "technologia"],
  },
  {
    id: "orbit-tech-learn-001",
    section: "orbit-action",
    title: "Nowa technologia: wybierz płytkę",
    instruction:
      "Wybierz dostępną technologię z legalnego pola siatki.",
    explanation:
      "Położenie płytki w siatce wpływa na koszt jej pozyskania.",
    next: "orbit-tech-learn-002",
    tags: ["orbita", "technologia", "pozyskanie"],
  },
  {
    id: "orbit-tech-learn-002",
    section: "orbit-action",
    title: "Nowa technologia: opłać koszt",
    instruction:
      "Opłać koszt pozyskania i umieść wymaganego kolonistę na polu akcji.",
    next: "orbit-tech-learn-003",
    tags: ["orbita", "technologia", "koszt"],
  },
  {
    id: "orbit-tech-learn-003",
    section: "orbit-action",
    title: "Nowa technologia: umieść płytkę",
    instruction:
      "Umieść technologię na najniższym dostępnym poziomie odpowiedniego miejsca na swojej planszy.",
    explanation:
      "Od tej chwili możesz jej używać, a przeciwnicy również mogą z niej korzystać, zapewniając ci premię.",
    next: "turn-end-001",
    tags: ["orbita", "technologia"],
  },
  {
    id: "orbit-tech-upgrade-001",
    section: "orbit-action",
    title: "Rozwój technologii: wybierz płytkę",
    instruction:
      "Wybierz jedną z posiadanych technologii, którą możesz przesunąć na wyższy poziom.",
    next: "orbit-tech-upgrade-002",
    tags: ["orbita", "technologia", "rozwój"],
  },
  {
    id: "orbit-tech-upgrade-002",
    section: "orbit-action",
    title: "Rozwój technologii: opłać koszt",
    instruction:
      "Opłać koszt wymagany do osiągnięcia wybranego poziomu.",
    warnings: [
      "Nie możesz przekroczyć maksymalnego poziomu technologii.",
    ],
    next: "orbit-tech-upgrade-003",
    tags: ["orbita", "technologia", "koszt"],
  },
  {
    id: "orbit-tech-upgrade-003",
    section: "orbit-action",
    title: "Rozwój technologii: przesuń płytkę",
    instruction:
      "Przesuń płytkę technologii na nowy poziom i od tej chwili stosuj zwiększoną wartość.",
    next: "turn-end-001",
    tags: ["orbita", "technologia", "poziom"],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // ORBITA – ZAOPATRZENIE
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "orbit-supply-001",
    section: "orbit-action",
    title: "Zaopatrzenie: wybierz rząd",
    instruction:
      "Wybierz legalny rząd magazynu orbitalnego.",
    explanation:
      "Siła akcji określa, ile elementów możesz pobrać.",
    next: "orbit-supply-002",
    tags: ["orbita", "zaopatrzenie", "magazyn"],
  },
  {
    id: "orbit-supply-002",
    section: "orbit-action",
    title: "Zaopatrzenie: pobierz elementy",
    instruction:
      "Pobierz dozwoloną liczbę zasobów lub kryształów z wybranego rzędu.",
    warnings: [
      "Nie przekraczaj limitu magazynu osobistego.",
      "Nowe kryształy odłóż do dolnej, niedostępnej części magazynu.",
    ],
    next: "orbit-supply-003",
    tags: ["orbita", "zasoby", "kryształy"],
  },
  {
    id: "orbit-supply-003",
    section: "orbit-action",
    title: "Zaopatrzenie: pozostaw magazyn bez uzupełniania",
    instruction:
      "Nie uzupełniaj teraz pobranych elementów, chyba że konkretny efekt nakazuje inaczej.",
    explanation:
      "Magazyn jest zwykle odnawiany podczas aktualizacji poziomu kolonii.",
    next: "turn-end-001",
    tags: ["orbita", "magazyn"],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // MARS – CENTRUM KONTROLI
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "mars-control-001",
    section: "mars-action",
    title: "Centrum kontroli: wybierz jednostki",
    instruction:
      "Zdecyduj, czy chcesz poruszyć boty, łazik, czy oba rodzaje jednostek zgodnie z siłą akcji.",
    choices: [
      { id: "bots", label: "Porusz boty", next: "mars-bot-001" },
      { id: "rover", label: "Porusz łazik", next: "mars-rover-001" },
    ],
    tags: ["Mars", "centrum kontroli", "ruch"],
  },
  {
    id: "mars-bot-001",
    section: "mars-action",
    title: "Ruch botów",
    instruction:
      "Wydaj dostępne punkty ruchu na przemieszczanie swoich botów.",
    explanation:
      "Każdy bot po ruchu wyznacza nową strefę budowy.",
    warnings: [
      "Nie przechodź przez pola lub obszary zakazane przez zasady mapy.",
    ],
    next: "mars-bot-002",
    tags: ["Mars", "bot", "ruch"],
  },
  {
    id: "mars-bot-002",
    section: "mars-action",
    title: "Sprawdź nową strefę budowy",
    instruction:
      "Po zakończeniu ruchu sprawdź, jakie puste heksy i budynki są teraz w zasięgu każdego bota.",
    next: "turn-end-001",
    tags: ["Mars", "bot", "budowa"],
  },
  {
    id: "mars-rover-001",
    section: "mars-action",
    title: "Ruch łazika: policz punkty",
    instruction:
      "Ustal podstawową liczbę punktów ruchu, dodaj premię technologii i ewentualny ruch kupowany za kryształy.",
    next: "mars-rover-002",
    tags: ["Mars", "łazik", "ruch"],
  },
  {
    id: "mars-rover-002",
    section: "mars-action",
    title: "Ruch łazika: wybierz trasę",
    instruction:
      "Przesuwaj łazik po mapie, wydając punkty ruchu.",
    explanation:
      "Po drodze możesz zbierać kryształy zgodnie z zasadami pól, a kończąc ruch na płytce badań lub odkrycia możesz ją zabrać.",
    next: "mars-rover-003",
    tags: ["Mars", "łazik", "trasa"],
  },
  {
    id: "mars-rover-003",
    section: "mars-action",
    title: "Ruch łazika: rozpatrz pole końcowe",
    instruction:
      "Jeżeli kończysz ruch na płytce badań lub odkrycia, zabierz ją i natychmiast rozpatrz efekt.",
    warnings: [
      "Nie możesz zakończyć ruchu na płytce wymagającej kosztu, którego nie potrafisz opłacić.",
    ],
    next: "turn-end-001",
    tags: ["Mars", "łazik", "badanie", "odkrycie"],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // MARS – BUDOWA
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "mars-build-001",
    section: "mars-action",
    title: "Budowa: wybierz typ",
    instruction:
      "Wybierz budynek podstawowy, który chcesz zbudować.",
    choices: [
      { id: "mine", label: "Kopalnia", next: "mars-build-002" },
      { id: "power", label: "Generator", next: "mars-build-002" },
      { id: "water", label: "Ekstraktor wody", next: "mars-build-002" },
      { id: "plant", label: "Szklarnia", next: "mars-build-002" },
      { id: "oxygen", label: "Kondensator tlenu", next: "mars-build-002" },
      { id: "shelter", label: "Schronienie", next: "mars-build-002" },
    ],
    tags: ["Mars", "budowa"],
  },
  {
    id: "mars-build-002",
    section: "mars-action",
    title: "Budowa: wskaż bota",
    instruction:
      "Wskaż własnego bota, którego strefa budowy obejmuje wybrany heks.",
    warnings: [
      "Łazik nie zapewnia strefy budowy.",
    ],
    next: "mars-build-003",
    tags: ["Mars", "budowa", "bot"],
  },
  {
    id: "mars-build-003",
    section: "mars-action",
    title: "Budowa: sprawdź legalność heksu",
    instruction:
      "Sprawdź, czy heks jest pusty, znajduje się w dozwolonej odległości i spełnia warunki wybranego typu budynku.",
    next: "mars-build-004",
    tags: ["Mars", "budowa", "heks"],
  },
  {
    id: "mars-build-004",
    section: "mars-action",
    title: "Budowa: sprawdź kompleks",
    instruction:
      "Sprawdź, czy nowa płytka będzie sąsiadowała z budynkiem tego samego typu.",
    choices: [
      { id: "separate", label: "Nie łączy się", next: "mars-build-006" },
      { id: "complex", label: "Tworzy lub powiększa kompleks", next: "mars-build-005" },
    ],
    tags: ["Mars", "budowa", "kompleks"],
  },
  {
    id: "mars-build-005",
    section: "mars-action",
    title: "Budowa: wybierz technologię",
    instruction:
      "Wskaż własną lub cudzą technologię odpowiedniego typu i na wymaganym poziomie.",
    explanation:
      "Jeżeli używasz cudzej technologii, jej właściciel otrzyma premię.",
    warnings: [
      "Bez odpowiedniej technologii nie możesz wykonać tej budowy jako części kompleksu.",
    ],
    next: "mars-build-006",
    tags: ["Mars", "budowa", "technologia", "kompleks"],
  },
  {
    id: "mars-build-006",
    section: "mars-action",
    title: "Budowa: opłać koszt",
    instruction:
      "Opłać koszt budynku oraz wszystkie dodatkowe koszty wynikające z zajęcia pola akcji lub użycia efektów.",
    next: "mars-build-007",
    tags: ["Mars", "budowa", "koszt"],
  },
  {
    id: "mars-build-007",
    section: "mars-action",
    title: "Budowa: połóż płytkę",
    instruction:
      "Połóż płytkę budynku na wybranym heksie i umieść swój znacznik własności, jeżeli wymaga tego typ budynku.",
    next: "mars-build-008",
    tags: ["Mars", "budowa", "płytka"],
  },
  {
    id: "mars-build-008",
    section: "mars-action",
    title: "Budowa: rozpatrz LSS",
    instruction:
      "Jeżeli zbudowałeś budynek LSS, przesuń odpowiadający mu znacznik na torze.",
    explanation:
      "Jeśli znacznik znajdował się poniżej obecnego poziomu kolonii, otrzymasz premię z kolumny oraz wybierzesz dostępną nagrodę LSS.",
    next: "mars-build-009",
    tags: ["Mars", "budowa", "LSS"],
  },
  {
    id: "mars-build-009",
    section: "mars-action",
    title: "Budowa: sprawdź aktualizację kolonii",
    instruction:
      "Sprawdź, czy wszystkie wymagane znaczniki LSS osiągnęły próg następnego poziomu kolonii.",
    choices: [
      { id: "no", label: "Nie", next: "turn-end-001" },
      { id: "yes", label: "Tak", next: "colony-update-001" },
    ],
    tags: ["Mars", "budowa", "poziom kolonii"],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // MARS – ULEPSZENIE
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "mars-upgrade-001",
    section: "mars-action",
    title: "Ulepszenie: wybierz projekt",
    instruction:
      "Wybierz posiadany, niezbudowany projekt.",
    checklist: [
      "Projekt odpowiada typowi wybranego budynku.",
      "Spełniasz wymaganie wielkości kompleksu.",
      "Wybrany budynek znajduje się w strefie twojego bota.",
    ],
    next: "mars-upgrade-002",
    tags: ["Mars", "ulepszenie", "projekt"],
  },
  {
    id: "mars-upgrade-002",
    section: "mars-action",
    title: "Ulepszenie: wybierz budynek",
    instruction:
      "Wskaż legalny budynek podstawowy zgodnego typu.",
    warnings: [
      "Nie każdy budynek w kompleksie musi należeć do ciebie, ale musisz spełnić dokładne warunki ulepszenia.",
    ],
    next: "mars-upgrade-003",
    tags: ["Mars", "ulepszenie", "budynek"],
  },
  {
    id: "mars-upgrade-003",
    section: "mars-action",
    title: "Ulepszenie: opłać koszt",
    instruction:
      "Opłać koszt projektu i akcji.",
    next: "mars-upgrade-004",
    tags: ["Mars", "ulepszenie", "koszt"],
  },
  {
    id: "mars-upgrade-004",
    section: "mars-action",
    title: "Ulepszenie: połóż znacznik",
    instruction:
      "Umieść swój znacznik budynku zaawansowanego na wybranym budynku podstawowym.",
    explanation:
      "Budynek podstawowy nadal pozostaje częścią kompleksu.",
    next: "mars-upgrade-005",
    tags: ["Mars", "ulepszenie", "budynek zaawansowany"],
  },
  {
    id: "mars-upgrade-005",
    section: "mars-action",
    title: "Ulepszenie: odwróć projekt",
    instruction:
      "Oznacz projekt jako zbudowany i pozostaw go przy planszy jako źródło akcji wykonawczej oraz punktów końcowych.",
    next: "turn-end-001",
    tags: ["Mars", "ulepszenie", "projekt"],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // MARS – STATEK
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "mars-ship-001",
    section: "mars-action",
    title: "Przyjęcie statku: sprawdź dostępność",
    instruction:
      "Sprawdź, czy na bieżącym poziomie kolonii dostępny jest statek do przyjęcia.",
    next: "mars-ship-002",
    tags: ["Mars", "statek"],
  },
  {
    id: "mars-ship-002",
    section: "mars-action",
    title: "Przyjęcie statku: opłać koszt",
    instruction:
      "Opłać koszt akcji i wybierz dostępny statek.",
    next: "mars-ship-003",
    tags: ["Mars", "statek", "koszt"],
  },
  {
    id: "mars-ship-003",
    section: "mars-action",
    title: "Przyjęcie statku: weź kolonistów",
    instruction:
      "Przenieś do kwater mieszkalnych liczbę kolonistów wynikającą ze statku i poziomu użytej technologii pojemności.",
    warnings: [
      "Nie przekraczaj liczby kolonistów dostępnych w swojej puli.",
    ],
    next: "mars-ship-004",
    tags: ["Mars", "statek", "koloniści"],
  },
  {
    id: "mars-ship-004",
    section: "mars-action",
    title: "Przyjęcie statku: odbierz bota",
    instruction:
      "Jeżeli wybrany statek lub efekt zapewnia bota, umieść go zgodnie z zasadami przyjęcia.",
    next: "mars-ship-005",
    tags: ["Mars", "statek", "bot"],
  },
  {
    id: "mars-ship-005",
    section: "mars-action",
    title: "Przyjęcie statku: umieść statek",
    instruction:
      "Umieść statek w swoim hangarze.",
    explanation:
      "Statek może później służyć do prywatnej podróży i jest wart punkty na końcu gry.",
    next: "turn-end-001",
    tags: ["Mars", "statek", "hangar"],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // MARS – NAUKOWIEC / KONTRAKT
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "mars-scientist-001",
    section: "mars-action",
    title: "Wybierz naukowca albo kontrakt",
    instruction:
      "Wybierz jeden dostępny element z oferty.",
    choices: [
      { id: "scientist", label: "Zatrudnij naukowca", next: "mars-scientist-002" },
      { id: "contract", label: "Weź kontrakt", next: "mars-contract-001" },
    ],
    tags: ["Mars", "naukowiec", "kontrakt"],
  },
  {
    id: "mars-scientist-002",
    section: "mars-action",
    title: "Naukowiec: opłać koszt",
    instruction:
      "Opłać koszt wskazany przy naukowcu i połóż jego kartę przy swojej planszy.",
    next: "mars-scientist-003",
    tags: ["Mars", "naukowiec", "koszt"],
  },
  {
    id: "mars-scientist-003",
    section: "mars-action",
    title: "Naukowiec: uruchom kontrakty",
    instruction:
      "Jeżeli jest to pierwszy zatrudniony naukowiec w grze, wyłóż kontrakty ziemskie zgodnie z zasadami.",
    next: "mars-scientist-004",
    tags: ["Mars", "naukowiec", "kontrakty"],
  },
  {
    id: "mars-scientist-004",
    section: "mars-action",
    title: "Naukowiec: umieść specjalistę",
    instruction:
      "Jeżeli możesz, wyślij znacznik naukowca do zgodnego budynku zaawansowanego albo zachowaj go zgodnie z zasadami.",
    explanation:
      "Naukowiec może udostępniać akcję wykonawczą bez kosztu kryształu i punktuje za pasujące budynki na końcu gry.",
    next: "turn-end-001",
    tags: ["Mars", "naukowiec", "budynek zaawansowany"],
  },
  {
    id: "mars-contract-001",
    section: "mars-action",
    title: "Kontrakt: wybierz kartę",
    instruction:
      "Wybierz dostępny kontrakt, którego warunek jesteś w stanie zrealizować.",
    warnings: [
      "Niewykonany kontrakt daje punkty ujemne.",
    ],
    next: "mars-contract-002",
    tags: ["Mars", "kontrakt"],
  },
  {
    id: "mars-contract-002",
    section: "mars-action",
    title: "Kontrakt: opłać pobranie",
    instruction:
      "Opłać kryształ wymagany za pobranie kontraktu.",
    warnings: [
      "Świeżo zdobyty w tej samej turze kryształ nie jest dostępny.",
    ],
    next: "mars-contract-003",
    tags: ["Mars", "kontrakt", "kryształ"],
  },
  {
    id: "mars-contract-003",
    section: "mars-action",
    title: "Kontrakt: połóż kartę",
    instruction:
      "Połóż kontrakt odkryty przy swojej planszy i od tej chwili śledź jego warunek.",
    explanation:
      "W kontrakcie dostawy możesz odkładać wymagane elementy na kartę. Nie można ich potem odzyskać.",
    next: "turn-end-001",
    tags: ["Mars", "kontrakt"],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // AKTUALIZACJA KOLONII
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "colony-update-001",
    section: "colony-update",
    title: "Przesuń poziom kolonii",
    instruction:
      "Przesuń znacznik kolonii o jeden poziom.",
    next: "colony-update-002",
    tags: ["kolonia", "aktualizacja"],
  },
  {
    id: "colony-update-002",
    section: "colony-update",
    title: "Uzupełnij technologie",
    instruction:
      "Dodaj nowe płytki technologii do siatki zgodnie z nowym poziomem kolonii.",
    next: "colony-update-003",
    tags: ["kolonia", "technologie"],
  },
  {
    id: "colony-update-003",
    section: "colony-update",
    title: "Uzupełnij magazyn",
    instruction:
      "Uzupełnij magazyn orbitalny zgodnie z zasadami nowego poziomu kolonii.",
    next: "colony-update-004",
    tags: ["kolonia", "magazyn"],
  },
  {
    id: "colony-update-004",
    section: "colony-update",
    title: "Udostępnij statki",
    instruction:
      "Dodaj nowe statki do obszaru dostępnego dla graczy, jeżeli przewiduje to nowy poziom.",
    next: "colony-update-005",
    tags: ["kolonia", "statki"],
  },
  {
    id: "colony-update-005",
    section: "colony-update",
    title: "Zaktualizuj projekty",
    instruction:
      "Usuń lub dodaj projekty zgodnie z zasadami aktualnego poziomu kolonii.",
    next: "colony-update-006",
    tags: ["kolonia", "projekty"],
  },
  {
    id: "colony-update-006",
    section: "colony-update",
    title: "Przesuń wahadłowiec",
    instruction:
      "Dostosuj pozycję zatrzymywania wahadłowca do nowego poziomu kolonii.",
    explanation:
      "Im bardziej samowystarczalna kolonia, tym rzadsze regularne loty.",
    next: "colony-update-007",
    tags: ["kolonia", "wahadłowiec"],
  },
  {
    id: "colony-update-007",
    section: "colony-update",
    title: "Zaktualizuj licznik misji",
    instruction:
      "Jeżeli nowy poziom zmniejsza liczbę misji wymaganych do końca gry, przesuń znacznik pozostałych misji.",
    next: "colony-update-008",
    tags: ["kolonia", "misje", "koniec gry"],
  },
  {
    id: "colony-update-008",
    section: "colony-update",
    title: "Punktuj obszar postępu",
    instruction:
      "Jeżeli ten poziom kolonii uruchamia punktowanie obszaru postępu, przyznaj punkty według aktualnych pozycji.",
    next: "colony-update-009",
    tags: ["kolonia", "postęp", "punktacja"],
  },
  {
    id: "colony-update-009",
    section: "colony-update",
    title: "Sprawdź koniec gry",
    instruction:
      "Sprawdź, czy liczba ukończonych misji jest teraz wystarczająca do uruchomienia końca gry.",
    choices: [
      { id: "no", label: "Nie", next: "turn-end-001" },
      { id: "yes", label: "Tak", next: "end-trigger-001" },
    ],
    tags: ["kolonia", "koniec gry"],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // KONIEC TURY I AKCJA WYKONAWCZA
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "turn-end-001",
    section: "turn-helper",
    title: "Czy chcesz wykonać akcję wykonawczą?",
    instruction:
      "W swojej turze możesz wykonać maksymalnie jedną akcję wykonawczą, przed albo po akcji głównej.",
    choices: [
      { id: "yes", label: "Tak", next: "executive-001" },
      { id: "no", label: "Nie", next: "turn-end-004" },
    ],
    tags: ["tura", "akcja wykonawcza"],
  },
  {
    id: "executive-001",
    section: "executive",
    title: "Wybierz źródło akcji wykonawczej",
    instruction:
      "Wybierz zbudowany projekt, technologię lub inny efekt, który udostępnia akcję wykonawczą.",
    next: "executive-002",
    tags: ["akcja wykonawcza", "projekt"],
  },
  {
    id: "executive-002",
    section: "executive",
    title: "Sprawdź naukowca",
    instruction:
      "Sprawdź, czy w budynku znajduje się pasujący naukowiec uprawniający cię do bezpłatnego użycia.",
    choices: [
      { id: "free", label: "Tak, używam dzięki naukowcowi", next: "executive-004" },
      { id: "pay", label: "Nie", next: "executive-003" },
    ],
    tags: ["akcja wykonawcza", "naukowiec"],
  },
  {
    id: "executive-003",
    section: "executive",
    title: "Opłać akcję kryształem",
    instruction:
      "Opłać wymagany kryształ, korzystając wyłącznie z dostępnej części magazynu.",
    next: "executive-004",
    tags: ["akcja wykonawcza", "kryształ"],
  },
  {
    id: "executive-004",
    section: "executive",
    title: "Wykonaj efekt",
    instruction:
      "Rozpatrz pełny efekt akcji wykonawczej.",
    warnings: [
      "Nie wykonuj drugiej akcji wykonawczej w tej samej turze.",
    ],
    next: "turn-end-004",
    tags: ["akcja wykonawcza"],
  },
  {
    id: "turn-end-004",
    section: "turn-helper",
    title: "Sprawdź cel prywatny",
    instruction:
      "Jeżeli spełniłeś warunek celu prywatnego i nie realizowałeś wcześniej innego celu, możesz teraz go zagrać.",
    choices: [
      { id: "complete", label: "Realizuję cel", next: "private-goal-001" },
      { id: "skip", label: "Nie realizuję", next: "turn-end-005" },
    ],
    tags: ["tura", "cel prywatny"],
  },
  {
    id: "private-goal-001",
    section: "private-goal",
    title: "Zrealizuj cel prywatny",
    instruction:
      "Pokaż kartę, potwierdź warunek i wybierz jedną z przedstawionych nagród.",
    warnings: [
      "W całej grze wolno zrealizować tylko jeden cel prywatny.",
    ],
    next: "turn-end-005",
    tags: ["cel prywatny", "nagroda"],
  },
  {
    id: "turn-end-005",
    section: "turn-helper",
    title: "Zakończ turę",
    instruction:
      "Pozostaw użytych kolonistów na polach akcji. Kolejny gracz rozpoczyna swoją turę.",
    explanation:
      "Świeżo zdobyte kryształy nadal pozostają w dolnej części magazynu do początku twojej następnej tury.",
    next: "round-check-001",
    tags: ["tura", "koniec tury"],
  },
  {
    id: "round-check-001",
    section: "round",
    title: "Czy wszyscy wykonali turę?",
    instruction:
      "Sprawdź, czy każdy gracz rozegrał jedną turę w tej fazie kolonizacji.",
    choices: [
      { id: "no", label: "Nie", next: "turn-001" },
      { id: "yes", label: "Tak", next: "shuttle-001" },
    ],
    tags: ["runda", "kolejność"],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // WAHADŁOWIEC
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "shuttle-001",
    section: "shuttle",
    title: "Przesuń wahadłowiec",
    instruction:
      "Przesuń wahadłowiec o liczbę pól wynikającą z aktualnego poziomu kolonii.",
    next: "shuttle-002",
    tags: ["wahadłowiec", "runda"],
  },
  {
    id: "shuttle-002",
    section: "shuttle",
    title: "Sprawdź kierunek lotu",
    instruction:
      "Ustal, czy wahadłowiec w tej rundzie leci z Orbity na Marsa, z Marsa na Orbitę, czy nie wykonuje lotu.",
    choices: [
      { id: "to-mars", label: "Lot na Marsa", next: "shuttle-to-mars-001" },
      { id: "to-orbit", label: "Lot na Orbitę", next: "shuttle-to-orbit-001" },
      { id: "none", label: "Brak lotu", next: "new-round-001" },
    ],
    tags: ["wahadłowiec", "kierunek"],
  },
  {
    id: "shuttle-to-mars-001",
    section: "shuttle",
    title: "Kto leci na Marsa?",
    instruction:
      "Każdy gracz znajdujący się na Orbicie decyduje, czy leci wahadłowcem, używa własnego statku, korzysta z innej możliwości, czy zostaje.",
    next: "shuttle-to-mars-002",
    tags: ["wahadłowiec", "Mars"],
  },
  {
    id: "shuttle-to-mars-002",
    section: "shuttle",
    title: "Ustal kolejność na Marsie",
    instruction:
      "Gracze przybywający na Marsa wybierają lub otrzymują pola kolejności zgodnie z kolejnością podróży.",
    next: "shuttle-to-mars-003",
    tags: ["wahadłowiec", "kolejność", "Mars"],
  },
  {
    id: "shuttle-to-mars-003",
    section: "shuttle",
    title: "Rozpatrz przybycie na Marsa",
    instruction:
      "Rozpatrz korzyści i obowiązki wynikające z wybranego pola oraz aktualnego poziomu kolonii.",
    next: "new-round-001",
    tags: ["wahadłowiec", "Mars", "przybycie"],
  },
  {
    id: "shuttle-to-orbit-001",
    section: "shuttle",
    title: "Kto leci na Orbitę?",
    instruction:
      "Każdy gracz znajdujący się na Marsie decyduje, czy leci wahadłowcem, używa własnego statku, czy zostaje.",
    next: "shuttle-to-orbit-002",
    tags: ["wahadłowiec", "orbita"],
  },
  {
    id: "shuttle-to-orbit-002",
    section: "shuttle",
    title: "Ustal kolejność na Orbicie",
    instruction:
      "Gracze przybywający na Orbitę zajmują pola kolejności zgodnie z zasadami podróży.",
    next: "shuttle-to-orbit-003",
    tags: ["wahadłowiec", "kolejność", "orbita"],
  },
  {
    id: "shuttle-to-orbit-003",
    section: "shuttle",
    title: "Rozpatrz przybycie na Orbitę",
    instruction:
      "Odbierz korzyści wynikające z pola kolejności i zakończ podróż.",
    next: "new-round-001",
    tags: ["wahadłowiec", "orbita", "przybycie"],
  },
  {
    id: "new-round-001",
    section: "round",
    title: "Rozpocznij nową rundę",
    instruction:
      "Rozpocznij kolejną fazę kolonizacji. Gracze wykonują tury według kolejności po swoich stronach planszy.",
    explanation:
      "Na początku swojej tury przenieś kryształy z dolnej części magazynu do części dostępnej.",
    next: "turn-001",
    tags: ["runda", "nowa runda"],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // MISJE I KONIEC GRY
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: "mission-check-001",
    section: "missions",
    title: "Sprawdź misję",
    instruction:
      "Po wykonaniu działania odpowiadającego misji zwiększ swój wkład, jeżeli spełniasz jej warunek.",
    next: "mission-check-002",
    tags: ["misje", "postęp"],
  },
  {
    id: "mission-check-002",
    section: "missions",
    title: "Odbierz kryształ za wkład",
    instruction:
      "Jeżeli twój wkład przesuwa znacznik zgodnie z misją, odbierz należny kryształ i umieść go w dolnej części magazynu.",
    next: "mission-check-003",
    tags: ["misje", "kryształ"],
  },
  {
    id: "mission-check-003",
    section: "missions",
    title: "Czy misja została ukończona?",
    instruction:
      "Jeżeli wspólny warunek misji został osiągnięty, oznacz ją jako ukończoną.",
    choices: [
      { id: "no", label: "Nie", next: "turn-end-001" },
      { id: "yes", label: "Tak", next: "mission-check-004" },
    ],
    tags: ["misje", "ukończenie"],
  },
  {
    id: "mission-check-004",
    section: "missions",
    title: "Zmniejsz liczbę pozostałych misji",
    instruction:
      "Przesuń znacznik pozostałych misji i sprawdź, czy uruchomiono koniec gry.",
    choices: [
      { id: "no", label: "Gra trwa", next: "turn-end-001" },
      { id: "yes", label: "Uruchomiono koniec", next: "end-trigger-001" },
    ],
    tags: ["misje", "koniec gry"],
  },
  {
    id: "end-trigger-001",
    section: "end-game",
    title: "Oznacz uruchomienie końca gry",
    instruction:
      "Dokończ bieżącą rundę. Następnie rozegraj jeszcze jedną pełną fazę kolonizacji.",
    warnings: [
      "Gra nie kończy się natychmiast.",
    ],
    next: "end-trigger-002",
    tags: ["koniec gry"],
  },
  {
    id: "end-trigger-002",
    section: "end-game",
    title: "Ostatnia runda",
    instruction:
      "Rozegraj ostatnią rundę. Po jej fazie kolonizacji pomiń fazę wahadłowca.",
    next: "end-001",
    tags: ["koniec gry", "ostatnia runda"],
  },
  {
    id: "end-001",
    section: "scoring",
    title: "Przygotuj punktację końcową",
    instruction:
      "Zakończ wszystkie bieżące efekty i przygotuj plansze graczy do punktowania.",
    next: "end-002",
    tags: ["punktacja", "koniec gry"],
  },
  {
    id: "end-002",
    section: "scoring",
    title: "Punktuj obszar postępu",
    instruction:
      "Przyznaj punkty za końcowe pozycje znaczników w obszarze postępu.",
    next: "end-003",
    tags: ["punktacja", "postęp"],
  },
  {
    id: "end-003",
    section: "scoring",
    title: "Punktuj technologie",
    instruction:
      "Przyznaj punkty za poziomy posiadanych technologii zgodnie z tabelą końcową.",
    next: "end-004",
    tags: ["punktacja", "technologie"],
  },
  {
    id: "end-004",
    section: "scoring",
    title: "Punktuj statki",
    instruction:
      "Przyznaj punkty za prywatne statki znajdujące się w hangarze.",
    next: "end-005",
    tags: ["punktacja", "statki"],
  },
  {
    id: "end-005",
    section: "scoring",
    title: "Punktuj projekty i budynki zaawansowane",
    instruction:
      "Dodaj punkty za zbudowane projekty i odejmij punkty za projekty pozostawione niezbudowane.",
    next: "end-006",
    tags: ["punktacja", "projekty"],
  },
  {
    id: "end-006",
    section: "scoring",
    title: "Przygotuj kolonistów",
    instruction:
      "Przenieś kolonistów wskazanych przez zasady końca gry do kwater mieszkalnych.",
    next: "end-007",
    tags: ["punktacja", "koloniści"],
  },
  {
    id: "end-007",
    section: "scoring",
    title: "Punktuj kolonistów",
    instruction:
      "Przyznaj punkty według najwyższego zajętego pola toru kwater mieszkalnych.",
    next: "end-008",
    tags: ["punktacja", "koloniści", "kwatery"],
  },
  {
    id: "end-008",
    section: "scoring",
    title: "Punktuj naukowców",
    instruction:
      "Każdy naukowiec punktuje za wszystkie pasujące budynki zaawansowane na Marsie, niezależnie od właściciela.",
    next: "end-009",
    tags: ["punktacja", "naukowcy"],
  },
  {
    id: "end-009",
    section: "scoring",
    title: "Punktuj kontrakty",
    instruction:
      "Dodaj punkty za wykonane kontrakty i odejmij punkty za niewykonane.",
    next: "end-010",
    tags: ["punktacja", "kontrakty"],
  },
  {
    id: "end-010",
    section: "scoring",
    title: "Ustal zwycięzcę",
    instruction:
      "Gracz z największą liczbą punktów OP wygrywa. Przy remisie zastosuj oficjalne kryteria rozstrzygające.",
    tags: ["punktacja", "zwycięzca", "remis"],
  },
];

export const onMarsGuidedById = Object.fromEntries(
  onMarsGuidedTutorial.map((step) => [step.id, step]),
) as Record<string, GuidedStep>;

export function getOnMarsGuidedStep(id: string): GuidedStep | undefined {
  return onMarsGuidedById[id];
}

export function getOnMarsGuidedSection(section: string): GuidedStep[] {
  return onMarsGuidedTutorial.filter((step) => step.section === section);
}

export function validateOnMarsGuidedTutorial(): string[] {
  const errors: string[] = [];
  const ids = new Set(onMarsGuidedTutorial.map((step) => step.id));

  for (const step of onMarsGuidedTutorial) {
    if (step.next && !ids.has(step.next)) {
      errors.push(`${step.id}: brak kroku next=${step.next}`);
    }

    for (const choice of step.choices ?? []) {
      if (!ids.has(choice.next)) {
        errors.push(`${step.id}/${choice.id}: brak kroku next=${choice.next}`);
      }
    }
  }

  return errors;
}
